/**
 * DictionaryPicker — wybór słownika (`dictionaries.code`) dla pola typu select/multiselect
 * w edytorach definicji pól (kreator pola dodatkowego modułu systemowego, pole kartoteki
 * systemowej w panelu ITE, pole tabeli dodatkowej).
 *
 * Renderuje `SearchableSelect` (wyszukiwarka po nazwie, opisie, kodzie i typie słownika)
 * zasilany `useDictionariesList()` (scalona lista lokalne+globalne — patrz
 * `hooks/useDictionary.ts`). Wartość = kod słownika, `null` gdy nic nie wybrano (pole korzysta
 * z wbudowanej listy opcji zamiast słownika) — czyszczenie przez „X" w polu. Opcje i plakietki
 * typu budują `dictionary-options.ts`.
 *
 * Nie filtruje po `origin`/`status` — każdy słownik z listy scalonej jest wybieralny;
 * konsument (rendering pola) i tak korzysta tylko z pozycji `selectable` w danym słowniku.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchableSelect } from '@/components/SearchableSelect';
import { useDictionariesList } from '@/hooks/useDictionary';

import { buildTenantDictionaryOptions, withUnavailableValueOption } from './dictionary-options';

export interface DictionaryPickerProps {
  /** Kod wybranego słownika, `null` gdy brak (pole używa opcji wpisanych ręcznie). */
  value: string | null;
  onChange: (code: string | null) => void;
  disabled?: boolean;
  id?: string;
  'data-testid'?: string;
}

export function DictionaryPicker({
  value,
  onChange,
  disabled,
  id,
  'data-testid': testId = 'dictionary-picker',
}: DictionaryPickerProps) {
  const { t } = useTranslation();
  const { data: dictionaries, isLoading } = useDictionariesList();

  const options = useMemo(() => {
    const built = buildTenantDictionaryOptions(dictionaries ?? [], t);
    // Opcja zastępcza „niedostępny" tylko dla listy, która faktycznie została pobrana — przy
    // ładowaniu i przy błędzie zapytania (`dictionaries === undefined`) nie wiemy, czy wartość
    // jest poza listą, więc nie oznaczamy jej jako niedostępnej.
    return dictionaries === undefined ? built : withUnavailableValueOption(built, value, t);
  }, [dictionaries, value, t]);

  return (
    <SearchableSelect
      id={id}
      data-testid={testId}
      aria-label={t('common.dictionaryPicker.label')}
      disabled={disabled || isLoading}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={t('common.dictionaryPicker.none')}
      allowClear
    />
  );
}
